/* client/src/components/Transactions.tsx */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { apiGetTransactions, apiAddTransaction, apiUpdateTransaction, apiDeleteTransaction } from '../services/apiService';
import { apiGetBusinessUnits } from '../services/apiService';
import { useMaterials } from '../contexts/MaterialsContext'; // Import the hook

interface Transaction {
  id: number;
  date: string;
  business_unit_id: number;
  business_unit_name: string;
  material_id: number;
  material_name: string;
  type: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  description: string;
}

interface BusinessUnit {
  id: number;
  name: string;
}

function Transactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  
  // Use materials from context instead of local state
  const { materials, loading: materialsLoading, error: materialsError, refreshMaterials } = useMaterials();
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    business_unit_id: '',
    material_id: '',
    type: 'purchase',
    quantity: '',
    unit_price: '',
    description: ''
  });

  useEffect(() => {
    fetchTransactions();
    fetchBusinessUnits();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await apiGetTransactions();
      setTransactions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const data = await apiGetBusinessUnits();
      setBusinessUnits(data);
    } catch (err: any) {
      console.error('Failed to fetch business units:', err);
      setBusinessUnits([]);
    }
  };

  const handleOpen = (transaction: Transaction | null = null) => {
    if (transaction) {
      setEditingTransaction(transaction);
      setFormData({
        date: transaction.date,
        business_unit_id: transaction.business_unit_id.toString(),
        material_id: transaction.material_id.toString(),
        type: transaction.type,
        quantity: transaction.quantity.toString(),
        unit_price: transaction.unit_price.toString(),
        description: transaction.description
      });
    } else {
      setEditingTransaction(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        business_unit_id: '',
        material_id: '',
        type: 'purchase',
        quantity: '',
        unit_price: '',
        description: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
  setOpen(false);
  setEditingTransaction(null);
  setFormData({
    date: new Date().toISOString().split('T')[0],
    business_unit_id: '',
    material_id: '',
    type: 'purchase',
    quantity: '',
    unit_price: '',
    description: ''
  });
};

// ADD THIS MISSING FUNCTION
const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  const { name, value } = e.target;
  setFormData(prev => ({ ...prev, [name]: value }));
};

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
    const transactionData = {
  date: formData.date,
  business_unit_id: parseInt(formData.business_unit_id),
  material_id: parseInt(formData.material_id),
  type: formData.type,
  quantity: parseFloat(formData.quantity),
  unit_price: parseFloat(formData.unit_price),
  total_amount: parseFloat(formData.quantity) * parseFloat(formData.unit_price),
  description: formData.description
};

    if (editingTransaction) {
      await apiUpdateTransaction(editingTransaction.id, transactionData);
    } else {
      await apiAddTransaction(transactionData);
    }
    
    fetchTransactions();
    handleClose();
  } catch (err: any) {
    setError(err.message || `Failed to ${editingTransaction ? 'update' : 'add'} transaction`);
  }
};

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await apiDeleteTransaction(id);
        fetchTransactions();
      } catch (err: any) {
        setError(err.message || 'Failed to delete transaction');
      }
    }
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Transactions Management</Typography>
        <Box>
          <Button 
            variant="outlined" 
            onClick={refreshMaterials}
            sx={{ mr: 2 }}
            startIcon={<RefreshIcon />}
            disabled={materialsLoading}
          >
            Refresh Materials
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Transaction
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {materialsError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => {}}>
          {materialsError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Business Unit</TableCell>
              <TableCell>Material</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{new Date(transaction.date).toLocaleDateString()}</TableCell>
                <TableCell>{transaction.business_unit_name}</TableCell>
                <TableCell>{transaction.material_name}</TableCell>
                <TableCell>
                  <Chip 
                    label={transaction.type} 
                    color={transaction.type === 'sale' ? "success" : "primary"} 
                    size="small" 
                  />
                </TableCell>
                <TableCell align="right">{transaction.quantity}</TableCell>
                <TableCell align="right">د.{transaction.unit_price.toFixed(2)}</TableCell>
                <TableCell align="right">
                  <Typography 
                    color={transaction.type === 'sale' ? "success.main" : "primary.main"}
                    fontWeight="bold"
                  >
                    د.{transaction.total_amount.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>{transaction.description}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(transaction)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(transaction.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {transactions.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No transactions found</Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="date"
              label="Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              name="business_unit_id"
              label="Business Unit"
              select
              fullWidth
              variant="outlined"
              value={formData.business_unit_id}
              onChange={handleChange}
              required
            >
              {businessUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="material_id"
              label="Material"
              select
              fullWidth
              variant="outlined"
              value={formData.material_id}
              onChange={handleChange}
              required
              disabled={materialsLoading}
            >
              {materials.map((material) => (
                <MenuItem key={material.id} value={material.id}>
                  {material.name} {/* Removed the price bracket part */}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="type"
              label="Type"
              select
              fullWidth
              variant="outlined"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <MenuItem value="purchase">Purchase</MenuItem>
              <MenuItem value="sale">Sale</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              name="quantity"
              label="Quantity"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.quantity}
              onChange={handleChange}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="unit_price"
              label="Unit Price"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.unit_price}
              onChange={handleChange}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="description"
              label="Description"
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTransaction ? 'Update' : 'Add'} Transaction
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default Transactions;